import { withSuppressedPermissionChecks } from '@frontend/user_authentication';
import { suppress_animate, suppress_undo_tracking, unsuppress_animate_if_ready, unsuppress_undo_tracking_if_ready } from '@frontend/utility';
import { Face, Edge, Vertex, PhiFile } from '@phi/jcore';
import App from '@frontend/application-container';
import { hide_sketches, show_sketches } from '@frontend/reference_sketches';
import { delete_vertex } from '@frontend/JCore/logging_jcore';
import { uil_change_object_visibility, uil_get_edges_of_vertex, uil_is_joint_smooth, uil_is_object_fixed, uil_undo, uil_unfix_objects } from '@frontend/ui_logic';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { uid_save_model } from '@frontend/input_output';
import { exportPhiModelAsStep, exportPhiModelToParasolid, listDocumentElements, log } from '@phi/frontend/api/calls';
import { invisible_objects } from '@phi/lattice';
import { has_selected_document, is_connected_to_onshape } from '@frontend/state';
import { download_step_file } from './api';
import { download_as_file } from '@phi/util/dom';
import { display_result_of_boolean_operation } from '@frontend/ui_display';

export async function export_as_step_to_download(filename: string) {
    if (App.state.currently_exporting_model)
        return new Error('Currently exporting a model as STEP. Please try again in a few moments.');
    log('Saving model as STEP', 'Saving model as STEP');
    App.state.currently_exporting_model = true;
    const invisible = invisible_objects();
    const file = generate_phi_file_for_export(invisible.faces, invisible.edges, invisible.vertices);
    const response = await download_step_file(sensible_file_name(filename), file);
    App.state.currently_exporting_model = false;
    if (response instanceof Error)
        return response;
    else
        return download_as_file(sensible_file_name(filename), 'application/octet-stream', response);
}

export async function export_as_step_to_onshape(export_name: string): Promise<undefined | Error> {
    if (App.state.currently_exporting_model)
        return new Error('Currently exporting a model to Onshape. Please try again in a few moments.');

    if (!is_connected_to_onshape())
        return new Error('Onshape connection not detected.');

    if (!has_selected_document())
        return new Error('Document not selected');

    const data = await listDocumentElements(App.state.document!, App.state.workspace!, true);
    if (data instanceof Error)
        return new Error('Connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref3]', { cause: data });

    const current_onshape_tab = data.find(x =>
        x.elementType === 'BLOB'
        && (
            x.name === export_name + '.STEP'
            || x.name === export_name + '_phi.STEP' // previously, we also sent the file extension as a name, so this is needed to avoid breaking the live update
        )
    );

    App.state.currently_exporting_model = true;

    const invisible = invisible_objects();

    const file = generate_phi_file_for_export(invisible.faces, invisible.edges, invisible.vertices);

    log('Export model as STEP to Onshape', 'Export model as STEP to Onshape');

    const response = await exportPhiModelAsStep(
        App.state.document!,
        current_onshape_tab?.id,
        App.state.workspace!,
        sensible_file_name(export_name),
        file,
    );

    log('Finieshed export model as STEP to Onshape', 'Finished export model as STEP to Onshape');

    App.state.currently_exporting_model = false;

    if (response instanceof Error) {
        return new Error('Failed to export model to Onshape. Connection with Onshape may have been lost. If necessary please save your work in a file, and refresh your browser.', { cause: response });
    } else {
        display_message_to_user(LOG.INFO, 'Your model <b>' + export_name + '</b> is being translated to an Onshape Part Studio.');
    }
}

export async function export_as_step_to_parasolid(export_name: string, body1id: number, body2id: number, operation: string, sendToOnshape: boolean): Promise<string | Error  > {
    if (App.state.currently_exporting_model)
        return new Error('Currently exporting a model to Onshape. Please try again in a few moments.');

    // Assemble data to send over
    let onshape_document_id = null;
    let onshape_tab_id = null;
    let onshape_workspace_id = null;

    if (is_connected_to_onshape()) {

        if (has_selected_document())
            onshape_document_id = App.state.document;
        else
            return new Error('Document not selected');

        const data = await listDocumentElements(App.state.document!, App.state.workspace!, true);
        if (data instanceof Error)
            return new Error('Connection with Onshape has been lost. If necessary please save your work in a file, and refresh your browser. [ref3]', { cause: data });

        const current_onshape_tab = data.find(x =>
            x.elementType === 'BLOB'
            && (
                x.name === export_name + '.STEP'
                || x.name === export_name + '_phi.STEP' // previously, we also sent the file extension as a name, so this is needed to avoid breaking the live update
            )
        );
        onshape_tab_id = current_onshape_tab?.id;

        onshape_workspace_id = App.state.workspace!;
    }

    if (sendToOnshape && !is_connected_to_onshape())
        log('Onshape connection not detected. Unable to send Parasolid output to Onshape', 'Onshape connection not detected. Unable to send Parasolid output to Onshape');

    App.state.currently_exporting_model = true;

    const invisible = invisible_objects();

    const file = generate_phi_file_for_export(invisible.faces, invisible.edges, invisible.vertices);

    log('Export model as STEP to Parasolid', 'Export model as STEP to Parasolid');

    log('Bodies: ', body1id + ', ' + body2id);

    const response = await exportPhiModelToParasolid(
            onshape_document_id,
            onshape_tab_id,
            onshape_workspace_id,
            sensible_file_name(export_name),
            file,
            body1id,
            body2id,
            operation,
            sendToOnshape ? 1 : 0
    );

    log('After export model as STEP to Parasolid', 'After export model as STEP to Parasolid');

    App.state.currently_exporting_model = false;

    try {
        if (response instanceof Error) {
            log('response is Error', 'Response is Error');
            return new Error('Failed to export model to Parasolid. Connection may have been lost. If necessary please save your work in a file, and refresh your browser.', { cause: response });
        }
        // Check if the response is a parsable JSON
        let parsedResponse: any;
        try {
            parsedResponse = JSON.parse(response);
        } catch (e) {
            log('response is not a valid JSON', 'Response is not a valid JSON');
            return new Error('Failed to process the response. Invalid JSON format.');
        }
        log('parsedResponse: ', parsedResponse);
        // Check if the parsed JSON contains an errorcode
        if (!parsedResponse) {
            log('response is null', 'response is null');
            return new Error('Operation failed due to null response');
        }

        if (isNaN(Number(parsedResponse.ResultErrorCode))) {
            log('result error code is not defined this should not happen', 'result error code is not defined this should not happen');
            return new Error('Operation failed due to malformed response');
        }
        const errorCode = Number(parsedResponse.ResultErrorCode);
        if (errorCode < 0) {
            log('boolean operation failed with error code ' + errorCode + ', boolean operation failed with error code '+ errorCode, 'boolean operation failed with error code ' + errorCode + ', boolean operation failed with error code '+ errorCode);
            //TODO: provide input to the user
            return new Error(`Operation ${operation} was not successfull`);
        }
        // No errors, continue processing the response
        display_message_to_user(LOG.INFO, 'Your model <b>' + export_name + '</b> has been processed for boolean operation '+ errorCode);
        //Check if the operation was successful and had a result which means a json is available
        if (errorCode != 0 || Number(parsedResponse.ResultAnalysisCode) != 0) {
            display_message_to_user(LOG.INFO, 'Boolean operation had no effect');
        } else {
            display_result_of_boolean_operation(parsedResponse.Result);
        }

        return parsedResponse.Result;

    } catch (unexpectedError) {
        // Handle unexpected errors
        log('Unexpected error occurred', 'Unexpected error occurred');
        return new Error('An unexpected error occurred. Please try again.');
    }

}

const sensible_file_name = (x: string): string =>
    ['.STEP', '.STP'].some(ext => x.toUpperCase().endsWith(ext))
        ? x
        : x + '.STEP';

const generate_phi_file_for_export = withSuppressedPermissionChecks(function generate_phi_file_for_export(
    faces_to_make_invisible: Array<Face>,
    edges_to_make_invisible: Array<Edge>,
    vertices_to_make_invisible: Array<Vertex>,
): PhiFile {
    // Create a scaling factor going from Phi unitless coordinates to coordinates set in the STEP file.
    // The STEP files we generate have meters as units.
    // The world_scaling_factor translates from unitless coordinates to the global coordinate unit which is cm.
    // Leave scale factor at 1.0 as we generate step files in cm instead of m from now on.
    const scale = 1.0;

    const faces_were_visible = App.state.showing_reference_sketches;

    // Remove all 2-vertices before exporting, and keep a list
    const dont_skip_pre_export_model_changes =
        !App.settings.split_faces
        || faces_to_make_invisible.length > 0
        || edges_to_make_invisible.length > 0
        || vertices_to_make_invisible.length > 0;

    if (dont_skip_pre_export_model_changes) {
        suppress_animate();
        suppress_undo_tracking();

        if (!App.settings.split_faces) {
            const vertices_to_unfix: Vertex[] = [];
            const vertices_to_delete: Vertex[] = [];
            for (const vid of uid_vertices.keys()) {
                const es = uil_get_edges_of_vertex(vid);
                if (es.length == 2 && uil_is_joint_smooth(es[0]!, es[1]!)) {
                    if (uil_is_object_fixed(vid))
                        vertices_to_unfix.push(vid);
                    vertices_to_delete.push(vid);
                }
            }
            uil_unfix_objects(vertices_to_unfix);
            for (const vid of vertices_to_delete)
                delete_vertex(vid, true);
        }

        if (faces_to_make_invisible.length > 0 || edges_to_make_invisible.length > 0 || vertices_to_make_invisible.length > 0) {
            show_sketches();
            uil_make_objects_invisible_for_export(faces_to_make_invisible, edges_to_make_invisible, vertices_to_make_invisible);
        }
    }

    const phi_model_description_string = uid_save_model(0, scale);

    if (dont_skip_pre_export_model_changes) {
        uil_make_objects_visible_after_export(faces_to_make_invisible, edges_to_make_invisible, vertices_to_make_invisible);
        unsuppress_undo_tracking_if_ready(false);
        // Add back all 2-vertices
        uil_undo();
        unsuppress_animate_if_ready();
        if (!faces_were_visible)
            hide_sketches();
    }

    return phi_model_description_string;
});

function uil_make_objects_invisible_for_export(face_ids: Face[], edge_ids: Edge[], vertex_ids: Vertex[]) {
    uil_change_object_visibility(face_ids, false);
    uil_change_object_visibility(edge_ids, false);
    uil_change_object_visibility(vertex_ids, false);
}

export function uil_make_objects_visible_after_export(face_ids: Face[], edge_ids: Edge[], vertex_ids: Vertex[]) {
    uil_change_object_visibility(face_ids, true);
    uil_change_object_visibility(edge_ids, true);
    uil_change_object_visibility(vertex_ids, true);
}
